/**
 * Manage DFV replay pitch result sequence
 */

import { getPlayType } from '@gameday/components/DynamicFieldView/utils/playEventHelper';
import playTypeMap from '@gameday/components/DynamicFieldView/utils/playTypeMap';
import { differenceInSeconds } from 'date-fns';
import logArgs from './logArgs';
let enableDFVLog = false;

if (typeof URLSearchParams !== 'undefined' && typeof location !== 'undefined') {
    const params = new URLSearchParams(location.search);
    const dfvLogParam = params.get('enableDFVLog');
    if (dfvLogParam === 'true') {
        enableDFVLog = dfvLogParam;
    }
}

const inPlayTestList = { isInPlayOuts: true, isInPlay: true };
const { NONE, PITCH, RESULT, RESULT_NO_IN_PLAY, PITCH_WITH_RESULT } = playTypeMap;

class PlaySequence {
    constructor (dfvPlay) {
        this.lastState = null;
        this.inPlay = dfvPlay?.inPlay;
        this.pitchTiming = dfvPlay?.pitchTiming || 0;
        this.resultTiming = dfvPlay?.resultTiming || 0;
        this.queue = {};
    }

    /**
     * Determines whether to hold the play when a new at-bat starts with no pitches.
     *
     * @param {Object} lastState - The previous play state.
     * @param {Object} currentPlay - The current play state.
     * @returns {boolean} - Returns `true` if the play should be held, otherwise `false`.
     */
    shouldHoldPlayOnNewAtBat (lastState, currentPlay) {
        const isNewAtBat = lastState?.currentPlay?.atBatIndex !== currentPlay?.atBatIndex;
        const isPitchState = lastState?.playType === PITCH;
        const hasNoPitches = currentPlay?.playEvents?.length === 0;
        if (lastState && isNewAtBat && lastState.playType !== NONE && hasNoPitches) {
            if (!isPitchState) {
                const { timerNum, delay } = this.getTimerByPlayType(lastState?.playType, lastState?.playStartTime, lastState?.playEndTime)?.adjust;
                const now = new Date();
                const lastDataCallTime = this.queue[lastState?.currentPlay?.atBatIndex]?.[RESULT];
                const secPassed = differenceInSeconds(now, lastDataCallTime);
                const shouldHoldup = secPassed < (timerNum + delay);
                const hasViewed = this.queue[lastState?.currentPlay?.atBatIndex].viewed;
                logArgs(enableDFVLog, 'anim data issue new atbat:', { timerNum, lastDataCallTime, test: !!(shouldHoldup && !hasViewed), currentPlay, lastState, secPassed, total: (timerNum + delay), shouldHoldup, hasViewed });
                return !!(shouldHoldup && !hasViewed);
            }
        }
    }

    /**
     * Determines whether the play should be held when the same data with result is received.
     * This is used to hold up the overlay and avoid triggering unnecessary updates.
     *
     * @param {Object} lastState - The previous play state.
     * @param {Object} currentPlay - The current play state.
     * @returns {boolean} - Returns `true` if the play should be held, otherwise `false`.
     */
    shouldHoldSamePlay (lastState, currentPlay) {
        // Incase we get the same data with result. return the previous state to hole up the overlay
        const lastAtBat = lastState?.currentPlay?.atBatIndex;
        const thisAtBat = currentPlay?.atBatIndex;
        if (lastAtBat === thisAtBat) {
            const { playType, playEndTime, playStartTime } = lastState;
            const validPitch = this.getPitch(currentPlay?.pitchIndex, this.inPlay);
            const result = currentPlay?.result?.event;
            const previousResult = lastState?.currentPlay?.result?.event;
            const { timerNum, delay } = this.getTimerByPlayType(playType, playStartTime, playEndTime)?.adjust;
            if (validPitch && result && (result === previousResult)) {
                const now = new Date();
                const lastDataCallTime = this.queue[lastAtBat]?.[RESULT];
                const secPassed = differenceInSeconds(now, lastDataCallTime);
                const shouldHoldup = secPassed < (timerNum + delay);
                const hasViewed = this.queue[lastAtBat]?.viewed;
                logArgs(enableDFVLog, 'anim data issue same data:', { test: !!(shouldHoldup && !hasViewed), currentPlay, lastState, secPassed, total: timerNum + delay, shouldHoldup, hasViewed });
                return !!(shouldHoldup && !hasViewed);
            }
        }

        return false;
    }

    /**
     * Determines whether a result should be created when a new at-bat starts with no pitches.
     *
     * @param {Object} lastState - The previous play state.
     * @param {Object} currentPlay - The current play state.
     * @returns {boolean} - Returns `true` if a result should be created, otherwise `false`.
     */
    shouldCreateResult (lastState, currentPlay) {
        const isNewAtBat = lastState?.currentPlay?.atBatIndex !== currentPlay?.atBatIndex;
        const hasNoPitches = currentPlay?.playEvents?.length === 0;
        logArgs(enableDFVLog, 'anim data issue: do we need result?', !!(lastState && isNewAtBat && hasNoPitches && lastState.playType === PITCH), { lastState, currentPlay, isNewAtBat, hasNoPitches });
        return !!(lastState && isNewAtBat && hasNoPitches && lastState.playType === PITCH);
    }

    /**
     * Helper function to determine the pitch start time and result end time based on the play type.
     *
     * @param {string} playType - The type of the current play (e.g., PITCH, RESULT, NONE).
     * @param {Object} validPitch - The valid pitch data containing the start time (if applicable).
     * @param {Date} playEndTime - The end time for the play (used for result plays).
     * @returns {Object} - Returns an object containing `pitchStartTime` and `resultEndTime`.
     */
    getPlayStartTimeHelper (playType, validPitch, playEndTime) {
        let pitchStartTime;
        let resultEndTime;
        if (playType === PITCH || playType === RESULT || playType === RESULT_NO_IN_PLAY || playType === PITCH_WITH_RESULT) {
            pitchStartTime = validPitch?.startTime;
            resultEndTime = playEndTime;
        } else if (playType === NONE) {
            pitchStartTime = undefined;
            resultEndTime = undefined;
        }

        return {
            pitchStartTime,
            resultEndTime
        };
    }

    /**
     * Retrieves the most recent pitch from the pitch index if it meets the conditions for being "in play".
     *
     * @param {Array} pitchIndex - The array of pitch objects, each containing pitch details.
     * @returns {Object|undefined} - Returns the last pitch object if it's valid and in play, otherwise `undefined`.
     */
    getPitch (pitchIndex, inPlayList = {}) {
        const inPlay = !enableDFVLog ? inPlayList : inPlayTestList;
        if (!Array.isArray(pitchIndex) || typeof inPlay !== 'object' || !inPlay?.isInPlay) return undefined;
        const shouldIncludeBoth = inPlay.isInPlayOuts === true;
        const includeIsInPlayOnly = inPlay.isInPlayOuts === false;
        const lastPitchIndex = pitchIndex?.[pitchIndex?.length - 1];
        const details = lastPitchIndex?.details;

        if (!details?.isInPlay) {
            return undefined;
        }

        if (shouldIncludeBoth && details?.isInPlay) {
            return lastPitchIndex;
        }

        if (includeIsInPlayOnly && (details?.isInPlay && !details?.isInPlayOuts)) {
            return lastPitchIndex;
        }

        return undefined;
    };

    /**
     * Retrieves the result from previous plays and creates a cloned state with the result.
     *
     * @param {Object} params - The object containing `allPlays` and `lastState`.
     * @param {Array} params.allPlays - The array of all plays, where the previous play is retrieved.
     * @param {Object} params.lastState - The last state used to create a new state with the result.
     * @returns {Object|undefined} - Returns the cloned state with the result or `undefined` if no valid result is found.
     */
    getResultFromPlays ({ allPlays, lastState }) {
        const previousPlay = allPlays[allPlays.length - 2];

        if (!previousPlay?.result?.event) {
            return undefined;
        }

        const cloned = { ...lastState };
        cloned.playType = RESULT;
        cloned.viewed = false;
        cloned.delay = 0;
        cloned.currentPlay = previousPlay;

        return cloned;
    }

    /**
     * Determines the flow of a play based on the current play and previous state.
     * It calculates various conditions and determines the appropriate play type.
     *
     * @param {Object} currentPlay - The current play object containing information about the play.
     * @returns {Object} - An object containing the determined play type, current play, play start time, and play end time.
     */
    getPerfectFlow (currentPlay) {
        const sameAtBat = currentPlay?.atBatIndex === this?.lastState?.currentPlay?.atBatIndex;
        const validPitch = this.getPitch(currentPlay?.pitchIndex, this.inPlay);
        const endTimeStamp = currentPlay?.about?.endTime;
        const hasNewResult = !!(sameAtBat && !this?.lastState?.currentPlay?.result?.event && currentPlay?.result?.event);
        const isNewerPlayEvent = !!(sameAtBat) && currentPlay?.playEvents.length > this.lastState?.currentPlay?.playEvents?.length;
        const isComplete = currentPlay?.about?.isComplete;
        const isHitInPlay = !!validPitch;
        const pitch = !!(!hasNewResult && isHitInPlay && isNewerPlayEvent);
        const resultAfterPitch = !!(hasNewResult && isHitInPlay && !isNewerPlayEvent);
        const justResult = !!(hasNewResult && isHitInPlay && isNewerPlayEvent);
        const playType = getPlayType({ isComplete, pitch, justResult, resultAfterPitch });

        const {
            pitchStartTime,
            resultEndTime
        } = this.getPlayStartTimeHelper(playType, validPitch, endTimeStamp);

        let pitchType;

        if (validPitch?.details) {
            const { isInPlayOuts, isInPlay } = validPitch.details;
            if (isInPlayOuts) {
                pitchType = 'Out of Play';
            } else if (isInPlay) {
                pitchType = 'In Play';
            }
        }

        return {
            playType,
            currentPlay,
            playStartTime: pitchStartTime,
            playEndTime: resultEndTime,
            pitchType
        };
    };

    /**
     * Returns an object with playType, currentPlay, playStartTime, and playEndTime based on the current play and previous state.
     * This function handles transitions and checks for conditions where the overlay should be held or a result needs to be created.
     *
     * @param {Object} currentPlay - The current play object containing the latest play data.
     * @param {Array} allPlays - An array of all the plays that have occurred.
     * @returns {Object} - An object containing the playType, currentPlay, playStartTime, and playEndTime.
     */
    getReplay ({ currentPlay, allPlays }) {
        const lastState = this.lastState;

        if (this.shouldCreateResult(lastState, currentPlay)) {
            const newData = this.getResultFromPlays({ allPlays, lastState });
            if (newData) {
                logArgs(enableDFVLog, 'Anim create result for smoother transition', newData);
                this.lastState = newData;
                return newData;
            }
        }

        const shouldHoldOverlay = this.shouldHoldSamePlay(lastState, currentPlay) || this.shouldHoldPlayOnNewAtBat(lastState, currentPlay);

        if (shouldHoldOverlay) {
            logArgs(enableDFVLog, 'Anim hold the current overlay', { lastState, currentPlay });
            this.updateQueue(lastState?.currentPlay?.atBatIndex, 'viewed', true);
            return lastState;
        }

        // Proceed with normal processing

        const newData = this.getPerfectFlow(currentPlay);
        this.lastState = newData;
        this.setQueue(newData?.currentPlay?.atBatIndex, newData?.playType);
        return newData;
    }

    getTimerByPlayType (playType, playStartTime, playEndTime) {
        let resultPlayDuration;
        let delayVideoStart = 0;
        const pitchTiming = this.pitchTiming;
        const resultTiming = this.resultTiming;
        const isResult = playType === PITCH_WITH_RESULT || playType === RESULT || playType === RESULT_NO_IN_PLAY;

        if (playStartTime && playEndTime) {
            resultPlayDuration = differenceInSeconds(new Date(playEndTime), new Date(playStartTime));
        }

        if (playType === PITCH_WITH_RESULT || playType === PITCH) {
            delayVideoStart = pitchTiming;
        }

        const resultTimingByPlayTime = resultPlayDuration && (resultPlayDuration > resultTiming) ? resultPlayDuration : resultTiming;
        const pitchTimingStr = `${pitchTiming}s`;
        return {
            standard: {
                timer: pitchTimingStr,
                timerNum: pitchTiming,
                delay: 0
            },
            adjust: {
                timer: !isResult ? pitchTimingStr : `${resultTimingByPlayTime}s`,
                timerNum: !isResult ? pitchTiming : resultTimingByPlayTime,
                delay: delayVideoStart
            }
        };
    }

    /**
     * Return an object with standard and adjust timer based on playType of the replay
     */
    getTimer ({ allPlays, currentPlay }) {
        const { playType, playEndTime, playStartTime } = this.getReplay({ allPlays, currentPlay });
        return this.getTimerByPlayType(playType, playStartTime, playEndTime);
    }

    reset () {
        this.lastState = null;
        this.queue = {};
    }

    setQueue (atBatIndex, playType) {
        if (typeof atBatIndex === 'undefined' || typeof playType === 'undefined' || playType === NONE) return;

        if (!this.queue[atBatIndex]) {
            this.queue[atBatIndex] = {};
        }

        const timestamp = new Date();

        if (playType === PITCH) {
            this.queue[atBatIndex][PITCH] = timestamp;
        } else {
            this.queue[atBatIndex][PITCH] ??= timestamp;
            this.queue[atBatIndex][RESULT] = timestamp;
        }
    }

    updateQueue (atBatIndex, prop, value) {
        if (this.queue[atBatIndex]?.[prop]) {
            this.queue[atBatIndex][prop] = value;
        }
    }
}

export default PlaySequence;
